import { Dispatch, SetStateAction, createContext } from 'react';
import {
  ExecutionVariable,
  SmartConnector,
  SmartConnectorBase,
  SmartConnectorMetaData,
  SmartConnectorStep,
  SQLScript,
} from '../../types';
import { SMART_CONNECTOR_STEPS, SMART_CONNECTOR_TYPES } from '../../constants';

import { VARIABLE_DATA_TYPES } from 'ts-components/inputs/ValueInput/types';

import { DeactivationModalProps } from '../../components/DeactivationModal';
import { ShowDeactivationModalWithResultFn } from '../hooks/useDeactivationModal';

export const defaultSmartConnector: SmartConnectorBase = {
  name: '',
  api_name: '',
  status: 'setup',
  connector_type: SMART_CONNECTOR_TYPES.spreadsheet,
  custom_object: null,
  error_notification_email: null,
  all_notification_email: null,
  flow: {
    additional_variables: [],
    loads: [],
  },
  is_diff_check_enabled: true,
  kizen_data_seeds: [],
  variables_to_exclude_from_diffs: [],
  max_concurrent_executions: 1,
};

type SQLScriptErrors = Partial<Record<keyof SQLScript, string[]>>;

export type SmartConnectorErrors = Partial<
  Omit<Record<keyof SmartConnector, string[]>, 'execution_variables'> & {
    execution_variables: Partial<Record<keyof ExecutionVariable, string[]>>[];
  } & SQLScriptErrors
>;

export type SmartConnectorPatchResult = [boolean | null, SmartConnector | null];

export type SmartConnectorContextType = {
  smartConnector: SmartConnector | SmartConnectorBase;
  loading: boolean;
  refreshing: boolean;
  step: SmartConnectorStep;
  setStep: Dispatch<SetStateAction<SmartConnectorStep>>;
  nextStep: SmartConnectorStep;
  setNextStep: Dispatch<SetStateAction<SmartConnectorStep>>;
  handleGoToStep: (step: SmartConnectorStep) => void;
  handleSave: (
    withClose: boolean,
    skipNavigation?: boolean
  ) => Promise<SmartConnectorPatchResult>;
  isNew: boolean;
  isDirty: boolean;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  isValid: boolean;
  setIsValid: Dispatch<SetStateAction<boolean>>;
  stepData: Partial<SmartConnector>;
  setStepData: Dispatch<SetStateAction<Partial<SmartConnector>>>;
  errors: SmartConnectorErrors;
  setErrors: Dispatch<SetStateAction<SmartConnectorErrors>>;
  handleUpdate: (promise: Promise<Partial<SmartConnector>>) => Promise<boolean>;
  refetchSmartConnector: () => Promise<void>;
  metaData: SmartConnectorMetaData;
  deactivationModalProps: DeactivationModalProps;
  showDeactivationModalWithResult: ShowDeactivationModalWithResultFn<SmartConnectorPatchResult>;
  forceResetScript: number;
  setForceResetScript: Dispatch<SetStateAction<number>>;
  templateScript: string;
  setTemplateScript: Dispatch<SetStateAction<string>>;
};

export const defaultContext: SmartConnectorContextType = {
  smartConnector: defaultSmartConnector,
  loading: true,
  refreshing: false,
  step: SMART_CONNECTOR_STEPS.general,
  setStep: () => {},
  nextStep: SMART_CONNECTOR_STEPS.general,
  setNextStep: () => {},
  handleGoToStep: () => {},
  handleSave: () => Promise.resolve([true, null]),
  isNew: false,
  isDirty: false,
  setIsDirty: () => {},
  stepData: {},
  setStepData: () => {},
  errors: {},
  setErrors: () => {},
  handleUpdate: async () => {
    return true;
  },
  refetchSmartConnector: async () => {},
  isValid: true,
  setIsValid: () => {},
  metaData: {
    execution_variables: {
      variable_data_types: [],
      array_delimiter: [],
      default_array_delimiter: ',',
      default_variable_data_type: VARIABLE_DATA_TYPES.string,
    },
    field_mapping_rules: {
      conflict_resolution: [],
      field_type_meta: {},
      variable_to_field_matching: {
        string: {
          array: {},
          single: {},
        },
      },
    },
    matching_rules: {
      match_archive_actions: [],
      multiple_match_actions: [],
      no_match_actions: [],
      single_match_actions: [],
    },
    kizen_data_seeds_allowed_field_types: [],
    execution_flow: {
      execution_status: {},
      step_status: {},
      group: {},
      step_outcome: {},
    },
    sql_versions: [],
    cadence_choices: [],
  },
  deactivationModalProps: {
    show: false,
    onHide: () => {},
    onConfirm: async () => {},
    defaultLeftBtnHandler: () => {},
  },
  showDeactivationModalWithResult: async () => [null, null],
  forceResetScript: 0,
  setForceResetScript: () => {},
  templateScript: '',
  setTemplateScript: () => {},
};

export const SmartConnectorContext =
  createContext<SmartConnectorContextType>(defaultContext);
